import {
  DueDiligenceRecordStatus,
  FatSeekingInterestPageInvestmentQuery,
  StrategyDetailsPageGetDueDiligenceRecordQuery
} from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import RadioButton from '@/components/basicComponents/radioButton';
import { INVESTMENT_QUERY } from '@/components/dealCatalog-page/seekingInterest-page/queries';
import { CREATE_DUE_DILIGENCE_RECORD_MUTATION } from '@/components/dueDiligence-page/queries';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import lodash from 'lodash';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { STRATEGY_DETAILS_PAGE_START_DUE_DILIGENCE_MUTATION } from '../../queries';

interface StartDueDiligenceProps {
  isOpen: boolean;
  funnelId: string;
  investmentData: FatSeekingInterestPageInvestmentQuery['Investment'] | null;
  dueDiligenceRecordRefetch: () => Promise<ApolloQueryResult<StrategyDetailsPageGetDueDiligenceRecordQuery>>;
  onClose: () => void;
}

const StartDueDiligence = ({ isOpen, funnelId, investmentData, dueDiligenceRecordRefetch, onClose }: StartDueDiligenceProps) => {
  const params = useParams();

  const { default: defaultErrorMessage } = useErrorMessages();
  const { handleSubmit, control } = useForm<{ status: string }>({ defaultValues: { status: 'Due Diligence' } });

  const [radioButtonsOptions, setRadioButtonsOptions] = useState(['Due Diligence']);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [startDueDiligence] = useMutation(STRATEGY_DETAILS_PAGE_START_DUE_DILIGENCE_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [INVESTMENT_QUERY]
  });
  const [createDueDiligenceRecord] = useMutation(CREATE_DUE_DILIGENCE_RECORD_MUTATION);

  useEffect(() => {
    if (!investmentData) return;

    setRadioButtonsOptions(['Due Diligence', `${lodash.startCase(investmentData.workflowInstance?.currentState?.label)} (current)`]);
  }, [investmentData]);

  const handleStartDueDiligence = async (data: { status: string }) => {
    try {
      setErrorMessage(null);
      setLoading(true);

      if (investmentData?.workflowInstance?.currentState?.name !== 'dueDiligence' && data.status === 'Due Diligence') {
        await startDueDiligence({
          variables: {
            investmentId: params?.strategyId
          }
        });
      }

      await createDueDiligenceRecord({
        variables: {
          data: {
            funnelId,
            investmentId: params?.strategyId,
            ownerEmail: investmentData?.investmentOwner?.email,
            status: DueDiligenceRecordStatus.Active
          }
        }
      });

      await dueDiligenceRecordRefetch();

      onClose();
    } catch {
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="Start Due Diligence"
      confirmButton={{
        name: 'Confirm',
        onClick: handleSubmit(handleStartDueDiligence),
        disabled: loading
      }}
      showCancelButton={!loading}
      showCloseIcon={!loading}
      isUseOutsideClick={!loading}
    >
      {loading ? (
        <ModalLoader />
      ) : (
        <>
          {errorMessage && (
            <Banner
              icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
              title="Error"
              description={errorMessage}
              bgColor={themeVar('context.error')}
            />
          )}
          <Subtitle>
            Do you want to update this strategy to <b>Due Diligence</b> or leave it set to{' '}
            <b>{lodash.startCase(investmentData?.workflowInstance?.currentState?.label)}</b>?
          </Subtitle>
          <Controller
            name="status"
            control={control}
            render={({ field: { onChange, value } }) => (
              <RadioButtonsWrapper>
                {radioButtonsOptions.map((option, index) => (
                  <RadioButton
                    key={option + index}
                    name="status"
                    radioButtonText={option}
                    selectedOption={value}
                    handleRadioChange={(event) => onChange(event.target.value)}
                  />
                ))}
              </RadioButtonsWrapper>
            )}
          />
        </>
      )}
    </ModalWrappedContent>
  );
};

export default StartDueDiligence;

const Subtitle = styled.p`
  text-align: center;
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  color: ${themeVar('font.base')};
`;

const RadioButtonsWrapper = styled.div`
  display: flex;
  padding-top: 25px;
`;
